import React from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useAddFirmware } from './addFirmware/useAddFirmware';
import { FirmwareUploadSection } from './addFirmware/FirmwareUploadSection';
import { FirmwareInfoSection } from './addFirmware/FirmwareInfoSection';
import { FirmwareFileSection } from './addFirmware/FirmwareFileSection';
import { FirmwareDeploymentSection } from './addFirmware/FirmwareDeploymentSection';
import './ScheduleUpdateModal.css';

interface ScheduleUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFirmwareCreated: () => void;
}

/**
 * The "add firmware" dialog: upload the image, describe the firmware, say
 * who gets it. The state and the rules live in `addFirmware/` and
 * `firmwareForm.ts`.
 */
export const ScheduleUpdateModal: React.FC<ScheduleUpdateModalProps> = ({ isOpen, onClose, onFirmwareCreated }) => {
  const { t } = useI18n();
  const state = useAddFirmware({ isOpen, onClose, onFirmwareCreated });

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={state.close}>
      <div className="schedule-update-modal schedule-update-modal-v2" data-testid="modal-add-firmware" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-title-section">
            <h2>{t('otaUpdates.addFirmwareVersion')}</h2>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={state.close} aria-label={t('common.close')}>×</button>
        </div>

        {state.error && (
          <div className="error-banner">
            <span>{state.error}</span>
            <button type="button" onClick={state.dismissError} aria-label={t('common.dismissError')}>×</button>
          </div>
        )}

        <form onSubmit={state.submit}>
          <div className="modal-content device-sections">
            <FirmwareUploadSection state={state} />
            <FirmwareInfoSection state={state} />
            <FirmwareFileSection state={state} />
            <FirmwareDeploymentSection state={state} />
          </div>

          <div className="modal-actions">
            <div className="action-group-left">
              <button type="button" onClick={state.close} className="btn btn-secondary" data-testid="btn-cancel" disabled={state.submitting}>
                {t('common.cancel')}
              </button>
            </div>
            <div className="action-group-right">
              <button type="submit" className="btn btn-primary" data-testid="btn-submit" disabled={state.submitting || state.uploading}>
                {state.submitting ? t('otaUpdates.adding') : t('otaUpdates.addFirmware')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
